import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// Material UI Components
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Divider,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    Stack,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import UpdateIcon from '@mui/icons-material/Update';

// Custom Components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import AaroLoadingScreen from 'components/Loading';

// Services
import { getOrders } from 'services/outside/aaro/dekont.js';
import { getProductions, postProduction, updateProduction } from 'services/api/production';
import { config } from 'Constants';
import { getTags } from 'services/api/general/tag';
import { notify } from 'helpers/notificationHelper';

// User info from localStorage
const user = JSON.parse(localStorage.getItem('user'));

// Function to add delay between operations
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Function to safely extract error message
const getErrorMessage = (error) => {
    if (!error) return 'Bilinmeyen hata';
    if (typeof error === 'string') return error;
    if (error.message) return error.message;
    if (error.response && error.response.data && error.response.data.message) return error.response.data.message;

    // Detailed axios error check
    if (error.response) {
        return `Sunucu hatası: ${error.response.status} - ${
            error.response.statusText || error.response.data || 'Bilinmeyen hata'
        }`;
    }
    if (error.request) {
        return `İstek hatası: Sunucudan yanıt alınamadı`;
    }

    // Try using Object.toString
    try {
        const errorCopy = { ...error };
        const seen = new WeakSet();
        const replacer = (key, value) => {
            if (typeof value === 'object' && value !== null) {
                if (seen.has(value)) {
                    return '[Döngüsel Referans]';
                }
                seen.add(value);
            }
            return value;
        };

        return JSON.stringify(errorCopy, replacer, 2) || 'Hata detayları gösterilemiyor';
    } catch (e) {
        try {
            const errorProps = Object.getOwnPropertyNames(error)
                .map((prop) => `${prop}: ${String(error[prop])}`)
                .join(', ');
            return `Hata özellikleri: ${errorProps}`;
        } catch (e2) {
            return 'Detaylı hata bilgisi alınamadı';
        }
    }
};

// Function to fetch all orders from Aaro
const fetchAllAaroOrders = async () => {
    const response = await getOrders({
        SirketID: 1,
        SiralamaKisiti: 'OlsTar:Desc',
        Durum: true,
        Sayfa: 1,
        SayfaSatirSayisi: 1000,
        Kapali: false,
    });

    return response?.data?.Model || [];
};

// Function to fetch all productions from our database
const fetchAllProductions = async () => {
    try {
        const response = await getProductions({
            limit: 1000,
            page: 1,
            isActive: true,
        });

        return response?.data?.result?.productions || [];
    } catch (error) {
        console.error('Üretimleri çekerken hata:', getErrorMessage(error));
        return [];
    }
};

function AaroSyncDialog({ open, onClose }) {
    const [syncStatus, setSyncStatus] = useState('idle');
    const [updateStatus, setUpdateStatus] = useState('idle');
    const [syncResults, setSyncResults] = useState(null);
    const [updateResults, setUpdateResults] = useState(null);
    const [displayCount, setDisplayCount] = useState(10);
    const [syncProgress, setSyncProgress] = useState([]);
    const [isDetailedView, setIsDetailedView] = useState(false);
    const queryClient = useQueryClient();

    // Fetch Aaro orders
    const {
        data: aaroOrders,
        isLoading: isLoadingAaroOrders,
        error: aaroOrdersError,
        refetch: refetchAaroOrders,
    } = useQuery({
        queryKey: ['aaroOrders'],
        queryFn: fetchAllAaroOrders,
        refetchOnWindowFocus: false,
        staleTime: 600000, // 10 minutes
    });

    // Fetch existing productions
    const {
        data: existingProductions,
        isLoading: isLoadingProductions,
        error: productionsError,
        refetch: refetchProductions,
    } = useQuery({
        queryKey: ['existingProductions'],
        queryFn: fetchAllProductions,
        refetchOnWindowFocus: false,
        staleTime: 600000, // 10 minutes
    });

    // Mutation for creating new productions
    const createProductionMutation = useMutation({
        mutationFn: (newProduction) => postProduction(newProduction),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['existingProductions'] });
        },
    });

    // Mutation for updating productions
    const updateProductionMutation = useMutation({
        mutationFn: (data) => updateProduction(data.id, data.updates),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['existingProductions'] });
        },
    });

    const handleSyncOrders = async () => {
        setSyncStatus('syncing');
        setSyncProgress([]); // Reset sync progress

        try {
            // Get existing aaroOrders IDs from our database
            const existingAaroOrderIds = existingProductions.map((prod) => prod.aaroOrder);
            setSyncProgress((prev) => [
                ...prev,
                {
                    type: 'info',
                    message: `Mevcut üretimler kontrol edildi: ${existingAaroOrderIds.length} adet üretim bulundu.`,
                },
            ]);

            // Filter Aaro orders that don't exist in our database
            const newAaroOrders = aaroOrders.filter((order) => !existingAaroOrderIds.includes(order.DekontID));
            setSyncProgress((prev) => [
                ...prev,
                {
                    type: 'info',
                    message: `Senkronize edilecek Aaro siparişleri tespit edildi: ${newAaroOrders.length} adet.`,
                },
            ]);

            // If no new orders, show message and return
            if (newAaroOrders.length === 0) {
                setSyncProgress((prev) => [
                    ...prev,
                    {
                        type: 'success',
                        message: 'Tüm Aaro siparişleri zaten veritabanı ile senkronize edilmiş durumda.',
                    },
                ]);
                setSyncResults({
                    success: true,
                    message: 'Tüm Aaro siparişleri zaten veritabanı ile senkronize.',
                    newOrdersCount: 0,
                    totalOrdersCount: aaroOrders.length,
                    existingOrdersCount: existingAaroOrderIds.length,
                });
                setSyncStatus('completed');
                return;
            }

            // Count of successful creations
            let successCount = 0;

            // Process each new order
            for (let i = 0; i < newAaroOrders.length; i++) {
                const order = newAaroOrders[i];

                setSyncProgress((prev) => [
                    ...prev,
                    {
                        type: 'process',
                        message: `${i + 1}/${newAaroOrders.length}: ${order.DekontID} numaralı sipariş işleniyor...`,
                    },
                ]);

                try {
                    // Create the new production
                    const newProduction = {
                        productionName: order.KartAdi || `Sipariş ${order.DekontID}`,
                        aaroOrder: order.DekontID,
                        aaroCustomerName: order.KartAdi,
                        aaroCustomerID: order.CariID,
                        responsibles: [],
                        isActive: true,
                        aaroFields: order,
                        // Add any other required fields for your production model
                    };

                    // Konsola veriyi yazdır (hata ayıklama için)
                    console.log(`${order.DekontID} için oluşturulacak üretim verisi:`, newProduction);

                    // Add the production to the database
                    await createProductionMutation.mutateAsync(newProduction);

                    successCount++;

                    setSyncProgress((prev) => [
                        ...prev,
                        {
                            type: 'success',
                            message: `${order.DekontID} numaralı sipariş başarıyla eklendi.`,
                        },
                    ]);

                    // Add a small delay to prevent API overload
                    await delay(500);
                } catch (error) {
                    setSyncProgress((prev) => [
                        ...prev,
                        {
                            type: 'error',
                            message: `${order.DekontID} numaralı sipariş eklenirken hata: ${getErrorMessage(error)}`,
                        },
                    ]);

                    // Add a longer delay after error
                    await delay(1000);
                }
            }

            // Check database status after sync
            try {
                setSyncProgress((prev) => [
                    ...prev,
                    {
                        type: 'info',
                        message: `Veritabanı durumu kontrol ediliyor...`,
                    },
                ]);

                await refetchProductions();
                const updatedProductions = await fetchAllProductions();

                setSyncProgress((prev) => [
                    ...prev,
                    {
                        type: 'success',
                        message: `Güncel veritabanı durumu: Toplam ${updatedProductions.length} üretim, ${
                            updatedProductions.filter((p) => p.aaroOrder).length
                        } tanesi Aaro siparişlerine bağlı.`,
                    },
                ]);
            } catch (error) {
                setSyncProgress((prev) => [
                    ...prev,
                    {
                        type: 'error',
                        message: `Veritabanı durumu kontrol edilirken hata: ${error.message}`,
                    },
                ]);
            }

            // Set final results
            setSyncResults({
                success: true,
                message: `${successCount} yeni sipariş başarıyla senkronize edildi.`,
                newOrdersCount: newAaroOrders.length,
                totalOrdersCount: aaroOrders.length,
                existingOrdersCount: existingAaroOrderIds.length,
                successCount,
            });

            // Show notification to user
            notify(true, `${successCount} yeni sipariş başarıyla senkronize edildi.`);
        } catch (error) {
            setSyncProgress((prev) => [
                ...prev,
                {
                    type: 'error',
                    message: `Senkronizasyon sırasında hata oluştu: ${error.message}`,
                },
            ]);
            console.error('Sync error:', error);
            setSyncResults({
                success: false,
                message: `Siparişleri senkronize ederken hata oluştu: ${error.message}`,
            });

            // Show error notification
            notify(false, `Senkronizasyon sırasında hata: ${error.message}`);
        }

        setSyncStatus('completed');
    };

    // Function to update aaroFields for existing productions
    const handleUpdateAaroFields = async () => {
        setUpdateStatus('updating');
        setSyncProgress([]); // Reset progress

        try {
            // Get existing aaroOrders IDs from our database
            const productionsWithAaroOrders = existingProductions.filter((prod) => prod.aaroOrder);

            setSyncProgress((prev) => [
                ...prev,
                {
                    type: 'info',
                    message: `Mevcut AARO bağlantılı üretimler kontrol edildi: ${productionsWithAaroOrders.length} adet üretim bulundu.`,
                },
            ]);

            if (productionsWithAaroOrders.length === 0) {
                setSyncProgress((prev) => [
                    ...prev,
                    {
                        type: 'info',
                        message: 'AARO bağlantılı üretim bulunamadı. Güncellenecek bir şey yok.',
                    },
                ]);
                setUpdateResults({
                    success: true,
                    message: 'AARO bağlantılı üretim bulunamadı.',
                    updatedCount: 0,
                    totalCount: 0,
                });
                setUpdateStatus('completed');
                return;
            }

            // Count of successful updates
            let successCount = 0;
            let failCount = 0;

            // Process each production with AARO link
            for (let i = 0; i < productionsWithAaroOrders.length; i++) {
                const production = productionsWithAaroOrders[i];

                // Find corresponding AARO order
                const aaroOrder = aaroOrders?.find((order) => order.DekontID === production.aaroOrder);

                if (!aaroOrder) {
                    setSyncProgress((prev) => [
                        ...prev,
                        {
                            type: 'warning',
                            message: `${production.aaroOrder} numaralı sipariş için AARO verisi bulunamadı.`,
                        },
                    ]);
                    failCount++;
                    continue;
                }

                setSyncProgress((prev) => [
                    ...prev,
                    {
                        type: 'process',
                        message: `${i + 1}/${productionsWithAaroOrders.length}: ${
                            production.aaroOrder
                        } numaralı sipariş güncelleniyor...`,
                    },
                ]);

                try {
                    // Update the production with current AARO data
                    await updateProductionMutation.mutateAsync({
                        id: production._id,
                        updates: {
                            aaroFields: aaroOrder,
                        },
                    });

                    successCount++;

                    setSyncProgress((prev) => [
                        ...prev,
                        {
                            type: 'success',
                            message: `${production.aaroOrder} numaralı sipariş başarıyla güncellendi.`,
                        },
                    ]);

                    // Add a small delay to prevent API overload
                    await delay(300);
                } catch (error) {
                    setSyncProgress((prev) => [
                        ...prev,
                        {
                            type: 'error',
                            message: `${production.aaroOrder} numaralı sipariş güncellenirken hata: ${getErrorMessage(
                                error
                            )}`,
                        },
                    ]);
                    failCount++;

                    // Add a longer delay after error
                    await delay(500);
                }
            }

            // Check database status after update
            try {
                await refetchProductions();

                setSyncProgress((prev) => [
                    ...prev,
                    {
                        type: 'success',
                        message: `Güncelleme tamamlandı: ${successCount} üretim başarıyla güncellendi, ${failCount} üretim güncellenemedi.`,
                    },
                ]);
            } catch (error) {
                setSyncProgress((prev) => [
                    ...prev,
                    {
                        type: 'error',
                        message: `Veritabanı durumu kontrol edilirken hata: ${error.message}`,
                    },
                ]);
            }

            // Set final results
            setUpdateResults({
                success: true,
                message: `${successCount} üretim başarıyla güncellendi.`,
                updatedCount: successCount,
                failedCount: failCount,
                totalCount: productionsWithAaroOrders.length,
            });

            // Show notification to user
            notify(true, `${successCount} üretim başarıyla güncellendi.`);
        } catch (error) {
            setSyncProgress((prev) => [
                ...prev,
                {
                    type: 'error',
                    message: `Güncelleme sırasında hata oluştu: ${error.message}`,
                },
            ]);
            console.error('Update error:', error);
            setUpdateResults({
                success: false,
                message: `Siparişleri güncellerken hata oluştu: ${error.message}`,
            });

            // Show error notification
            notify(false, `Güncelleme sırasında hata: ${error.message}`);
        }

        setUpdateStatus('completed');
    };

    const refreshData = () => {
        refetchAaroOrders();
        refetchProductions();
        setSyncStatus('idle');
        setUpdateStatus('idle');
        setSyncResults(null);
        setUpdateResults(null);
        setSyncProgress([]);
    };

    const handleShowMore = () => {
        setDisplayCount((prev) => prev + 10);
    };

    // Find if a production exists with the given Aaro order ID
    const findProductionByAaroOrder = (aaroOrderId) => {
        return existingProductions?.find((prod) => prod.aaroOrder === aaroOrderId);
    };

    // Get a list of Aaro orders that don't have corresponding productions
    const missingProductions =
        aaroOrders?.filter((order) => !existingProductions?.some((prod) => prod.aaroOrder === order.DekontID)) || [];

    // Loading state
    if (isLoadingAaroOrders || isLoadingProductions) {
        return (
            <Dialog open={open} fullWidth maxWidth="md">
                <DialogTitle>
                    AARO Senkronizasyonu
                    <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <AaroLoadingScreen />
                </DialogContent>
            </Dialog>
        );
    }

    // Error handling
    if (aaroOrdersError || productionsError) {
        return (
            <Dialog open={open} fullWidth maxWidth="md">
                <DialogTitle>
                    AARO Senkronizasyonu
                    <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <MDTypography variant="h5" color="error">
                        Veri yüklenirken hata oluştu: {(aaroOrdersError || productionsError).message}
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={onClose} color="dark">
                        Kapat
                    </MDButton>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={syncStatus === 'syncing' || updateStatus === 'updating' ? undefined : onClose}
            fullWidth
            maxWidth="lg"
        >
            <DialogTitle>
                Aaro Sipariş Senkronizasyonu
                {syncStatus !== 'syncing' && updateStatus !== 'updating' && (
                    <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                )}
            </DialogTitle>
            <DialogContent>
                <MDBox mb={2}>
                    <MDTypography variant="body2" color="text">
                        Aaro siparişlerini üretim veritabanı ile senkronize edin.
                    </MDTypography>
                </MDBox>

                <Divider />

                <MDBox mt={3}>
                    <Grid container spacing={3}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 4,
                            }}
                        >
                            <MDBox p={2} border="1px solid #eee" borderRadius="lg" bgcolor="#f1f8e9">
                                <MDTypography variant="h6">Toplam Sipariş</MDTypography>
                                <MDTypography variant="h3" textAlign="center" py={2} color="success">
                                    {aaroOrders?.length || 0}
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                    Aaro sistemindeki toplam sipariş sayısı
                                </MDTypography>
                            </MDBox>
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                md: 4,
                            }}
                        >
                            <MDBox p={2} border="1px solid #eee" borderRadius="lg" bgcolor="#e3f2fd">
                                <MDTypography variant="h6">Mevcut Üretim</MDTypography>
                                <MDTypography variant="h3" textAlign="center" py={2} color="info">
                                    {existingProductions?.filter((p) => p.aaroOrder).length || 0}
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                    Veritabanımızdaki Aaro bağlantılı üretim sayısı
                                </MDTypography>
                            </MDBox>
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                md: 4,
                            }}
                        >
                            <MDBox
                                p={2}
                                border="1px solid #eee"
                                borderRadius="lg"
                                bgcolor={missingProductions.length > 0 ? '#fff8e1' : '#f1f8e9'}
                            >
                                <MDTypography variant="h6">Senkronize Edilecek</MDTypography>
                                <MDTypography
                                    variant="h3"
                                    textAlign="center"
                                    py={2}
                                    color={missingProductions.length > 0 ? 'warning' : 'success'}
                                >
                                    {missingProductions.length}
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                    Veritabanına eklenecek sipariş sayısı
                                </MDTypography>
                            </MDBox>
                        </Grid>
                    </Grid>

                    <MDBox mt={3} display="flex" justifyContent="center">
                        <Stack direction="row" spacing={2}>
                            <MDButton
                                variant="contained"
                                color="info"
                                onClick={handleSyncOrders}
                                disabled={
                                    syncStatus === 'syncing' ||
                                    updateStatus === 'updating' ||
                                    missingProductions.length === 0
                                }
                                startIcon={<RefreshIcon />}
                            >
                                {syncStatus === 'syncing' ? 'Senkronize Ediliyor...' : 'Siparişleri Senkronize Et'}
                            </MDButton>
                            <MDButton
                                variant="outlined"
                                color="secondary"
                                onClick={handleUpdateAaroFields}
                                disabled={
                                    updateStatus === 'updating' ||
                                    syncStatus === 'syncing' ||
                                    existingProductions?.filter((p) => p.aaroOrder).length === 0
                                }
                                startIcon={<UpdateIcon />}
                            >
                                {updateStatus === 'updating' ? 'Güncelleniyor...' : 'Mevcut Siparişleri Güncelle'}
                            </MDButton>
                        </Stack>
                    </MDBox>
                </MDBox>

                {/* Sync or Update Results */}
                {(syncResults || updateResults) && (
                    <MDBox
                        mt={4}
                        p={2}
                        border="1px solid #eee"
                        borderRadius="lg"
                        bgcolor={syncResults?.success || updateResults?.success ? '#f1f8e9' : '#ffebee'}
                    >
                        <MDTypography
                            variant="h6"
                            color={syncResults?.success || updateResults?.success ? 'success' : 'error'}
                        >
                            {syncResults?.message || updateResults?.message}
                        </MDTypography>

                        {syncResults?.success && (
                            <MDBox mt={2}>
                                <Grid container spacing={2}>
                                    <Grid size={4}>
                                        <MDTypography variant="body2">
                                            Toplam Sipariş: {syncResults.totalOrdersCount}
                                        </MDTypography>
                                    </Grid>
                                    <Grid size={4}>
                                        <MDTypography variant="body2">
                                            Mevcut Üretim: {syncResults.existingOrdersCount}
                                        </MDTypography>
                                    </Grid>
                                    <Grid size={4}>
                                        <MDTypography variant="body2">
                                            Yeni Eklenen: {syncResults.successCount || 0}
                                        </MDTypography>
                                    </Grid>
                                </Grid>
                            </MDBox>
                        )}

                        {updateResults?.success && (
                            <MDBox mt={2}>
                                <Grid container spacing={2}>
                                    <Grid size={4}>
                                        <MDTypography variant="body2">
                                            Toplam Üretim: {updateResults.totalCount}
                                        </MDTypography>
                                    </Grid>
                                    <Grid size={4}>
                                        <MDTypography variant="body2">
                                            Başarılı Güncelleme: {updateResults.updatedCount || 0}
                                        </MDTypography>
                                    </Grid>
                                    <Grid size={4}>
                                        <MDTypography variant="body2">
                                            Başarısız Güncelleme: {updateResults.failedCount || 0}
                                        </MDTypography>
                                    </Grid>
                                </Grid>
                            </MDBox>
                        )}

                        <MDBox mt={2}>
                            <MDButton
                                variant="outlined"
                                size="small"
                                onClick={() => setIsDetailedView(!isDetailedView)}
                            >
                                {isDetailedView ? 'Detayları Gizle' : 'Detayları Göster'}
                            </MDButton>
                        </MDBox>
                    </MDBox>
                )}

                {/* Sync Progress Details */}
                {syncProgress.length > 0 && (
                    <MDBox
                        mt={4}
                        p={2}
                        border="1px solid #eee"
                        borderRadius="lg"
                        display={
                            isDetailedView || syncStatus === 'syncing' || updateStatus === 'updating' ? 'block' : 'none'
                        }
                    >
                        <MDTypography variant="h6" mb={2}>
                            {syncStatus === 'syncing' ? 'Senkronizasyon Süreci' : 'Güncelleme Süreci'}
                        </MDTypography>

                        <MDBox maxHeight="300px" overflow="auto" p={1} border="1px solid #eee" borderRadius="md">
                            {syncProgress.map((step, index) => (
                                <MDBox
                                    key={index}
                                    mb={1}
                                    p={1}
                                    borderRadius="sm"
                                    bgcolor={
                                        step.type === 'success'
                                            ? 'rgba(76, 175, 80, 0.1)'
                                            : step.type === 'error'
                                            ? 'rgba(244, 67, 54, 0.1)'
                                            : step.type === 'warning'
                                            ? 'rgba(255, 152, 0, 0.1)'
                                            : step.type === 'process'
                                            ? 'rgba(33, 150, 243, 0.1)'
                                            : 'rgba(158, 158, 158, 0.1)'
                                    }
                                >
                                    <MDTypography
                                        variant="body2"
                                        color={
                                            step.type === 'success'
                                                ? 'success'
                                                : step.type === 'error'
                                                ? 'error'
                                                : step.type === 'warning'
                                                ? 'warning'
                                                : step.type === 'process'
                                                ? 'info'
                                                : 'text'
                                        }
                                    >
                                        {index + 1}. {step.message}
                                    </MDTypography>
                                </MDBox>
                            ))}
                        </MDBox>
                    </MDBox>
                )}
            </DialogContent>
            <DialogActions>
                <MDButton
                    onClick={refreshData}
                    color="info"
                    startIcon={<RefreshIcon />}
                    disabled={syncStatus === 'syncing' || updateStatus === 'updating'}
                >
                    Yenile
                </MDButton>
                <MDButton
                    onClick={onClose}
                    color="dark"
                    disabled={syncStatus === 'syncing' || updateStatus === 'updating'}
                >
                    Kapat
                </MDButton>
            </DialogActions>
        </Dialog>
    );
}

export default AaroSyncDialog;
